/**
 * Observability query helpers shared by the ops pages and the mocks.
 *
 * `namesBusinessObjects` ports `subsystems/platform/facade/adapters/observability-api/src/guard.rs` so the UI can
 * warn before sending that a query needs a reason. The server stays authoritative:
 * the UI always reacts to `REASON_REQUIRED` too.
 */

export const MAX_QUERY_CHARS = 2000;
export const MAX_RANGE_DAYS = 30;
export const MAX_POINTS = 11_000;
export const MAX_LOG_LINES = 5000;

function isEmail(tok: string): boolean {
  const t = tok.replace(/^[^\p{L}\p{N}@._+-]+|[^\p{L}\p{N}@._+-]+$/gu, '');
  const at = t.indexOf('@');
  if (at <= 0) return false;
  const d = t.slice(at + 1);
  return d.includes('.') && !d.startsWith('.') && !d.endsWith('.');
}

function isUuidLike(tok: string): boolean {
  const t = tok.replace(/[^0-9a-fA-F-]/g, '');
  return t.length >= 32 && /^[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}$/i.test(t);
}

const NOT_DOMAIN_PREFIXES = new Set(['http', 'service', 'db', 'rpc', 'net', 'workflow', 'registry', 'otel', 'span', 'k8s']);

function looksLikeDomain(tok: string): boolean {
  const t = tok.replace(/^[^\p{L}\p{N}.-]+|[^\p{L}\p{N}.-]+$/gu, '');
  const labels = t.split('.');
  const tld = labels[labels.length - 1] ?? '';
  return (
    labels.length >= 2 &&
    labels.every((l) => l !== '' && /^[A-Za-z0-9-]+$/.test(l)) &&
    tld.length >= 2 &&
    /^[A-Za-z]+$/.test(tld) &&
    !NOT_DOMAIN_PREFIXES.has(labels[0] ?? '')
  );
}

/** Whether a query names domains, people or identifiers (then `X-Ops-Reason` is required). */
export function namesBusinessObjects(q: string): boolean {
  const longestDigits = Math.max(0, ...(q.match(/\d+/g) ?? []).map((m) => m.length));
  return q.split(/[\s"'`=~,{}()]+/).some((t) => isEmail(t) || isUuidLike(t) || looksLikeDomain(t)) || longestDigits >= 9;
}

// ---------------------------------------------------------------------------
// Time ranges (spec §99)

export type RangePreset = '5m' | '15m' | '1h' | '6h' | '24h' | '7d' | '30d';

export const RANGE_PRESETS: readonly { id: RangePreset; label: string; seconds: number }[] = [
  { id: '5m', label: '5 minutes', seconds: 300 },
  { id: '15m', label: '15 minutes', seconds: 900 },
  { id: '1h', label: '1 hour', seconds: 3600 },
  { id: '6h', label: '6 hours', seconds: 6 * 3600 },
  { id: '24h', label: '24 hours', seconds: 86_400 },
  { id: '7d', label: '7 days', seconds: 7 * 86_400 },
  { id: '30d', label: '30 days', seconds: 30 * 86_400 },
];

/** A preset (relative to "now" at query time) or an absolute custom window. */
export type TimeRange = { kind: 'preset'; preset: RangePreset } | { kind: 'custom'; start: string; end: string };

export function isPreset(value: string | null): value is RangePreset {
  return RANGE_PRESETS.some((p) => p.id === value);
}

export function resolveRange(range: TimeRange, now: number = Date.now()): { start: Date; end: Date } {
  if (range.kind === 'custom') return { start: new Date(range.start), end: new Date(range.end) };
  const seconds = RANGE_PRESETS.find((p) => p.id === range.preset)?.seconds ?? 3600;
  return { start: new Date(now - seconds * 1000), end: new Date(now) };
}

/** Validates a range like `guard::check_range` does, returning a message or null. */
export function rangeProblem(start: Date, end: Date): string | null {
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 'Start and end must be valid dates.';
  if (end <= start) return 'End must be after start.';
  if (end.getTime() - start.getTime() > MAX_RANGE_DAYS * 86_400_000) return `At most ${MAX_RANGE_DAYS} days per query.`;
  return null;
}

/** A step (seconds) giving ~240 points, never below the server minimum (>= 15 s, <= 11,000 points). */
export function defaultStep(start: Date, end: Date): number {
  const span = Math.max(1, (end.getTime() - start.getTime()) / 1000);
  const min = Math.max(1, Math.ceil(span / MAX_POINTS));
  return Math.max(15, min, Math.round(span / 240));
}

/** Range in URL form: `range=1h` or `from=<iso>&to=<iso>`. */
export function rangeFromParams(params: URLSearchParams, fallback: RangePreset = '1h'): TimeRange {
  const from = params.get('from');
  const to = params.get('to');
  if (from && to) return { kind: 'custom', start: from, end: to };
  const preset = params.get('range');
  return { kind: 'preset', preset: isPreset(preset) ? preset : fallback };
}

export function rangeToParams(range: TimeRange, params: URLSearchParams): void {
  params.delete('range');
  params.delete('from');
  params.delete('to');
  if (range.kind === 'preset') params.set('range', range.preset);
  else {
    params.set('from', range.start);
    params.set('to', range.end);
  }
}

// ---------------------------------------------------------------------------
// Deep links between the consoles (spec §116)

/** A preset (relative to now) or a fixed window, e.g. around a past registry command. */
export function logsLink(query: string, range?: RangePreset | TimeRange): string {
  const p = new URLSearchParams({ q: query });
  if (range) rangeToParams(typeof range === 'string' ? { kind: 'preset', preset: range } : range, p);
  return `/ops/logs?${p.toString()}`;
}

export function traceSearchLink(tags: Record<string, string>, range?: RangePreset | TimeRange): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(tags)) p.set(`tag.${k}`, v);
  if (range) rangeToParams(typeof range === 'string' ? { kind: 'preset', preset: range } : range, p);
  return `/ops/traces?${p.toString()}`;
}

export function correlationLink(id: string): string {
  return `/ops/correlate/${encodeURIComponent(id)}`;
}

/** The log query the Observability API suggests for a correlation id. */
export function correlationLogQuery(id: string): string {
  return `{service=~".+"} | json | correlation_id="${id}"`;
}

export const TRACE_ID_RE = /^[0-9a-f]{32}$/i;
